import _metadataHelper from "./helpers/metadata.js";
import _extractXmlMetadata from "./helpers/extractXmlMetadata.js";
import _notesHelper from "./helpers/notes.js";
import _xmlFormat from "xml-formatter";
import _formats from "../lib/formats.js";
import _tcRound from "./eventGroups/tcRound.js";
import _removeDuplicateBlankEvents from "./eventGroups/removeDuplicateBlankEvents.js";
import _subtractIncode from "./special/subtractIncode.js";
import _convertSpeakersToObject from "./eventGroups/convertSpeakersToObject.js";
import _decodeHtmlEvg from "./eventGroups/decodeHtmlEvg.js";
import _applyWindowOffsets from "./special/applyWindowOffsets.js";
import _defaults from "../lib/defaults.js";
import _decoder from "../profiles/index.js";
import _EventGroup from "../classes/eventGroup.js";
const EventGroup = _EventGroup;
const decoder = _decoder;
const defaults = _defaults;
const applyWindowOffsets = _applyWindowOffsets;
const decodeHtmlEvg = _decodeHtmlEvg;
const convertSpeakersToObject = _convertSpeakersToObject;
const subtractIncode = _subtractIncode;
const removeDuplicateBlankEvents = _removeDuplicateBlankEvents;
const tcRound = _tcRound;
const formats = _formats;
const xmlFormat = _xmlFormat;
const notesHelper = _notesHelper;
const extractXmlMetadata = _extractXmlMetadata;
const metadataHelper = _metadataHelper;
export default (async function (input, options = new defaults.options()) {
  if (decoder[options.profile]) {
    let eventGroup = new EventGroup();
    let sourceExt = formats.defaultExtMap[options.profile];
    let extractedNotes = null;
    let extractedMetadata = {};
    if (options.profile && ["xml", "ttml", "dfxp", "itt", "imscr"].indexOf(sourceExt) > -1) {
      //Remove everything before the <?xml tag
      input = input.substring(input.indexOf("<?xml"));
      /* Extract document level metadata (language, title, etc.) from the header */
      extractedMetadata = extractXmlMetadata(input);
      /* Replace tt:tt with tt */
      input = input.replace(/tt:tt/g, "tt");
      /* Replace tt:head with head */
      input = input.replace(/tt:head/g, "head");
      /* Replace tt:body with body */
      input = input.replace(/tt:body/g, "body");
      /* Replace tt:div with div */
      input = input.replace(/tt:div/g, "div");
      /* Replace tt:p with p */
      input = input.replace(/tt:p/g, "p");
      /* Replace tt:span with span */
      input = input.replace(/tt:span/g, "span");
      /* Replace tt:br with br */
      input = input.replace(/tt:br/g, "br");
      /* Replace tt:style with style */
      input = input.replace(/tt:style/g, "style");
      /* Replace tt:layout with layout */
      input = input.replace(/tt:layout/g, "layout");
      /* Replace tt:region with region */
      input = input.replace(/tt:region/g, "region");
      /* Replace tt:metadata with metadata */
      input = input.replace(/tt:metadata/g, "metadata");
      /* Replace tt:styling with styling */
      input = input.replace(/tt:styling/g, "styling");

      /* Extract cue-level annotation comments (eg. <p>...</p><!--note-->)
         before minify so they can be attached to events after decoding. */
      extractedNotes = notesHelper.extractXmlCueNotes(input);

      //For ITT keep collapseContent off so that significant whitespace next to
      //inline <span> elements is preserved (e.g. "</span> is" instead of "</span>is").
      //Minify still drops whitespace-only text between two spans ("</span> <span>"),
      //so encode those single-line spaces first. They are decoded back with the text.
      input = input.replace(/(<\/span>)([ \t]+)(?=<span\b)/gi, (match, close, spaces) => close + "&#32;".repeat(spaces.length));
      input = xmlFormat.minify(input, {
        collapseContent: sourceExt !== "itt" && sourceExt !== "imscr" && options.profile !== "ebuTtD"
      });
    } else if (options.profile === "broadstreamTtml") {
      /* Broadstream TTML is XML-based but is not part of the defaultExtMap XML group */
      extractedNotes = notesHelper.extractXmlCueNotes(input);
      extractedMetadata = extractXmlMetadata(input);
    }
    /* Pre-Process Decode */
    input = decoder[options.profile].preProcess.decode(input, options);
    /* Decode */
    eventGroup.events = await decoder[options.profile].decode(input, options);
    /* Remember the order events appear in the source file (later steps sort by time). Used by the Automatic QC sequentialTiming test. */
    const sourceOrder = eventGroup.events.map(event => event.id);
    /* Attach cue-level annotations (notes) extracted from the raw XML */
    if (extractedNotes) {
      notesHelper.attachXmlCueNotes(eventGroup.events, extractedNotes);
    }
    /* Store header metadata using the standard keys. When several fields map to the same key the first one found wins. */
    Object.entries(extractedMetadata).forEach(([name, value]) => {
      if (metadataHelper.getMetadata(eventGroup, name) === undefined) {
        metadataHelper.setMetadata(eventGroup, name, value);
      }
    });
    /* Post-Process Decode */
    eventGroup = decoder[options.profile].postProcess.decode(eventGroup, options);
    /* Keep the Event Group language in sync with the language found in the source file */
    const sourceLanguage = metadataHelper.getMetadata(eventGroup, "language");
    if (sourceLanguage) {
      eventGroup.language = sourceLanguage;
    }
    /* Decode HTML */
    eventGroup = decodeHtmlEvg(eventGroup);
    /* Converter Speakers to Speaker Objects */
    eventGroup = convertSpeakersToObject(eventGroup);
    /* Remove back to back blank events and ensure the first blank event is only 1 frame long */
    eventGroup = removeDuplicateBlankEvents(eventGroup, options.frameRate);
    /* Snap absolute cue times to nearest frame when requested (must run before subtractIncode). */
    if (options.jobInfo && (options.jobInfo.frame_snap === true || options.jobInfo.frame_snap === "true")) {
      eventGroup = tcRound(eventGroup, options.frameRate, options.dropFrame);
    }
    /* Remove incode from start/end times */

    eventGroup = subtractIncode(eventGroup, options);
    // console.log(JSON.stringify(eventGroup, null, 2));

    /* Apply Window Offset and Return Event Group */
    eventGroup = applyWindowOffsets(eventGroup, options.window, "subtract");
    //Return Event Group
    eventGroup.events.forEach((event, i, events) => {
      events[i] = {
        ...event
      };
    });

    // console.log(JSON.stringify(eventGroup, null, 2));

    const decoded = {
      ...eventGroup
    };
    /* Non-enumerable so it never ends up in serialized projects or Event Group copies */
    Object.defineProperty(decoded, "sourceOrder", {
      value: sourceOrder,
      enumerable: false,
      writable: true,
      configurable: true
    });
    return decoded;
  } else {
    throw new Error(`Source profile [${options.profile}] does not exist. Please ensure name matches a supported decoding profile. Profile names are case-sensitive.`);
  }
});